import {useTranslation} from 'react-i18next'
import {
  ANIMATION_DIRECTIONS,
  ANIMATION_MAX_SPEED,
  ANIMATION_MIN_SPEED,
  BG_ANIMATION_TYPES,
  CONTENT_ANIMATION_TYPES,
  SLIDESHOW_TEMPLATES,
  VIDEO_FPS_OPTIONS,
  VIDEO_MAX_DURATION_SEC,
  VIDEO_MIN_DURATION_SEC,
  type CreativeAnimation,
  type CreativeAnimationDirection,
  type CreativeBgAnimationType,
  type CreativeContentAnimationType,
  type CreativeTemplateId,
} from '../../../common/constants/creativeAssetSpecs'
import type {CreativeBackgroundMode} from '../../../common/utils/creativeAssetRenderer'
import type {CreativeVideoSettings} from '../../../common/utils/creativeVideoRenderer'
import {Slider} from './layerControls'

interface AnimationPanelProps {
  settings: CreativeVideoSettings
  bgMode: CreativeBackgroundMode
  template: CreativeTemplateId
  onChange: (patch: Partial<CreativeVideoSettings>) => void
}

/** Gradients can only sweep their angle; solid colors have nothing to animate. */
const bgTypesFor = (bgMode: CreativeBackgroundMode): readonly CreativeBgAnimationType[] => {
  if (bgMode === 'image') return BG_ANIMATION_TYPES
  if (bgMode === 'gradient') return ['none', 'pan']
  return ['none']
}

interface AnimationControlProps<T extends string> {
  title: string
  i18nPrefix: string
  types: readonly T[]
  animation: CreativeAnimation<T>
  directions: (type: T) => readonly CreativeAnimationDirection[]
  onChange: (animation: CreativeAnimation<T>) => void
}

/** Type, direction and speed for one animated layer. */
const AnimationControl = <T extends string>({
  title,
  i18nPrefix,
  types,
  animation,
  directions,
  onChange,
}: AnimationControlProps<T>) => {
  const {t} = useTranslation()
  const type = types.includes(animation.type) ? animation.type : types[0]
  const allowedDirections = directions(type)

  const handleType = (next: T) => {
    const nextDirections = directions(next)
    const direction = nextDirections.includes(animation.direction)
      ? animation.direction
      : (nextDirections[0] ?? animation.direction)
    onChange({...animation, type: next, direction})
  }

  return (
    <div className="caa-anim-group">
      <h3 className="caa-section-title">{title}</h3>
      <div className="caa-tabs">
        {types.map((option) => (
          <button
            key={option}
            type="button"
            className={`caa-tab${type === option ? ' is-active' : ''}`}
            onClick={() => handleType(option)}
          >
            {t(`${i18nPrefix}.${option}`)}
          </button>
        ))}
      </div>
      {allowedDirections.length > 1 && (
        <div className="caa-choice">
          <span className="caa-label">{t(`creativeAssets.animation.optionLabels.${type}`)}</span>
          <div className="caa-tabs caa-tabs-compact">
            {allowedDirections.map((direction) => (
              <button
                key={direction}
                type="button"
                className={`caa-tab${animation.direction === direction ? ' is-active' : ''}`}
                onClick={() => onChange({...animation, direction})}
              >
                {t(`creativeAssets.animation.directions.${direction}`)}
              </button>
            ))}
          </div>
        </div>
      )}
      {type !== 'none' && (
        <Slider
          label={t('creativeAssets.animation.speed')}
          value={animation.speed}
          min={ANIMATION_MIN_SPEED}
          max={ANIMATION_MAX_SPEED}
          step={1}
          format={(v) => `${v}×`}
          onChange={(speed) => onChange({...animation, speed})}
        />
      )}
    </div>
  )
}

/** Video animation settings — background, content and timing — shown under the preview. */
const AnimationPanel = ({settings, bgMode, template, onChange}: AnimationPanelProps) => {
  const {t} = useTranslation()
  const gradientBg = bgMode === 'gradient'
  // Slideshow needs screenshots; endless scrolling only exists for the screenshot strip.
  const contentTypes: readonly CreativeContentAnimationType[] = [
    ...CONTENT_ANIMATION_TYPES,
    ...(SLIDESHOW_TEMPLATES.includes(template) ? (['slideshow'] as const) : []),
    ...(template === 'strip' ? (['scroll'] as const) : []),
  ]

  return (
    <div className="caa-animation">
      <h2 className="caa-panel-title">{t('creativeAssets.animation.title')}</h2>
      <div className="caa-anim-grid">
        <AnimationControl
          title={t('creativeAssets.animation.background')}
          i18nPrefix="creativeAssets.animation.bgTypes"
          types={bgTypesFor(bgMode)}
          animation={settings.bg}
          directions={(type) => (gradientBg ? [] : ANIMATION_DIRECTIONS[type])}
          onChange={(bg) => onChange({bg})}
        />
        <AnimationControl
          title={t('creativeAssets.animation.content')}
          i18nPrefix="creativeAssets.animation.contentTypes"
          types={contentTypes}
          animation={settings.content}
          directions={(type) => ANIMATION_DIRECTIONS[type]}
          onChange={(content) => onChange({content})}
        />
        <div className="caa-anim-group">
          <h3 className="caa-section-title">{t('creativeAssets.animation.timing')}</h3>
          <Slider
            label={t('creativeAssets.video.duration')}
            value={settings.durationSec}
            min={VIDEO_MIN_DURATION_SEC}
            max={VIDEO_MAX_DURATION_SEC}
            step={1}
            format={(v) => `${v} s`}
            onChange={(durationSec) => onChange({durationSec})}
          />
          <div className="caa-tabs caa-tabs-compact">
            {VIDEO_FPS_OPTIONS.map((fps) => (
              <button
                key={fps}
                type="button"
                className={`caa-tab${settings.fps === fps ? ' is-active' : ''}`}
                onClick={() => onChange({fps})}
              >
                {fps} fps
              </button>
            ))}
          </div>
          <p className="cvt-hint">{t('creativeAssets.animation.speedHint')}</p>
        </div>
      </div>
    </div>
  )
}

export default AnimationPanel
